import React from "react";
// Prosumer Dashboard

// Default

// Mui
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid2";

// Custom
import Layout from "../../layout";
import ContainedButton from "../../forms/button/ContainedButton";
import CustomOutlinedSelect from "../../forms/select/OutlinedCustomSelect";
import ProsumerDashboardLineChart from "../charts/ProsumerDashboardLineChart";
import ProsumerDashboardBarChart from "../charts/ProsumerDashboardBarChart";

// React icons
import { HiOutlineDotsHorizontal } from "react-icons/hi";
import { MdOutlineProductionQuantityLimits } from "react-icons/md";
import { UnifiedIcon } from "../Icon/UnifiedIcon";

// Types
interface ProsumerDashboardItem {
  title: string;
  energy?: string;
  money?: string;
  percentage: number;
  increament: string;
  type: string;
}

interface SelectOption {
  value: string;
  label: string;
}

const ProsumerDashboard: React.FC = () => {
  const prosumerDashboardData: ProsumerDashboardItem[] = [
    {
      title: "Total Energy Produced",
      energy: "500",
      percentage: 85,
      increament: "12.08",
      type: "production",
    },
    {
      title: "Total Energy Consumed",
      energy: "350",
      percentage: 75,
      increament: "08.12",
      type: "consumption",
    },
    {
      title: "Total Energy Transacted",
      energy: "200",
      percentage: 45,
      increament: "08.12",
      type: "transaction",
    },
    {
      title: "Total Revenue Earned",
      money: "1500",
      percentage: 80,
      increament: "08.12",
      type: "earning",
    },
  ];

  const getStrokeColor = (type: string): string => {
    switch (type) {
      case "production":
        return "#ffffff";
      case "consumption":
        return "#43E97B";
      case "transaction":
        return "#FF8C42";
      case "earning":
        return "#0396FF";
      default:
        return "#e6e6e6";
    }
  };

  const circularViewBox = (percentage: number, type: string) => {
    return (
      <Box
        sx={{
          position: "relative",
          width: "80px",
          height: "80px",
          display: "inline-flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <svg
          viewBox="0 0 36 36"
          style={{
            transform: "rotate(-90deg)",
            width: "100%",
            height: "100%",
          }}
        >
          <circle
            cx="18"
            cy="18"
            r="16"
            fill="none"
            stroke={type === "production" ? "#ffffff" : "#e6e6e6"}
            strokeWidth="1"
          />
          <circle
            cx="18"
            cy="18"
            r="16"
            fill="none"
            stroke={getStrokeColor(type)}
            strokeWidth="4"
            strokeDasharray={`${percentage} 100`}
            strokeLinecap="round"
          />
        </svg>

        <Typography
          variant="body2"
          sx={{
            position: "absolute",
            color: type === "production" ? "#ffffff" : "inherit",
          }}
        >
          {percentage}%
        </Typography>
      </Box>
    );
  };

  const selectOptions: SelectOption[] = [
    { value: "Daily", label: "Daily" },
    { value: "Weekly", label: "Weekly" },
    { value: "Monthly", label: "Monthly" },
  ];

  return (
    <Layout>
      <Card>
        <CardHeader
          title={<Typography variant="h5">Prosumer Dashboard</Typography>}
        />
        <Divider />

        <CardContent>
          <Grid container spacing={2.25}>
            {prosumerDashboardData?.map((item, index) => {
              return (
                <Grid key={index} size={{ xs: 12, md: 3, lg: 3 }}>
                  <Card
                    sx={{
                      border: "1px solid #D7DAE7",
                      padding: "16px",
                      background:
                        item.type === "production" ? "#0396FF" : "inherit",
                    }}
                  >
                    <Stack direction={"row"} justifyContent={"space-between"}>
                      <Stack
                        direction={"row"}
                        alignItems={"center"}
                        gap={"10px"}
                      >
                        <Box
                          sx={{
                            background:
                              item.type === "production"
                                ? "#28A6FF"
                                : "#D7DAE7",
                            width: "fit-content",
                            padding: "7px 7px 0 7px",
                            borderRadius: "5px",
                          }}
                        >
                          <UnifiedIcon
                            icon={MdOutlineProductionQuantityLimits}
                            color={
                              item.type === "production" ? "#ffffff" : "#999b9d"
                            }
                            fontSize="30px"
                          />
                        </Box>

                        <Typography
                          variant="h5"
                          color={
                            item.type === "production" ? "#ffffff" : "inherit"
                          }
                          fontWeight={500}
                          width={"150px"}
                          lineHeight={"25px"}
                        >
                          {item.title}
                        </Typography>
                      </Stack>

                      <UnifiedIcon
                        icon={HiOutlineDotsHorizontal}
                        color={
                          item.type === "production" ? "#ffffff" : "#000000"
                        }
                        fontSize="25px"
                      />
                    </Stack>

                    <Stack
                      direction={"row"}
                      justifyContent={"space-between"}
                      alignItems={"center"}
                      padding={"0 20px"}
                      mt={"10px"}
                    >
                      <Typography
                        variant="h5"
                        fontWeight={500}
                        fontSize={"25px"}
                        color={
                          item.type === "production" ? "#ffffff" : "inherit"
                        }
                      >
                        {item.money && <span>&#8377;</span>}
                        {item.energy || item.money}
                        {item.energy && (
                          <Typography
                            component="span"
                            fontSize={"12px"}
                            fontWeight={400}
                          >
                            kWh
                          </Typography>
                        )}
                      </Typography>

                      {circularViewBox(item.percentage, item.type)}
                    </Stack>

                    <Stack
                      direction={"row"}
                      justifyContent={"space-between"}
                      alignItems={"center"}
                      mt={"20px"}
                    >
                      <Typography
                        variant="body1"
                        color={
                          item.type === "production" ? "#ffffff" : "inherit"
                        }
                      >
                        +{item.increament}%
                      </Typography>
                      <ContainedButton
                        label={"Last Month"}
                        bgColor={
                          item.type === "production"
                            ? "#ffffff"
                            : "linear-gradient(180deg, #1976d2 0%, #0396FF 100%)"
                        }
                        color={
                          item.type === "production" ? "#0396FF" : "#ffffff"
                        }
                        hoverBgColor={
                          item.type === "production"
                            ? "#ffffff"
                            : "linear-gradient(180deg, #75D3FB 0%, #28A6FF 100%)"
                        }
                        sx={{
                          width: "fit-content",
                        }}
                      />
                    </Stack>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mt: "20px" }}>
        <CardHeader
          title={<Typography variant="h5">Real Time Data</Typography>}
        />
        <Divider />

        <CardContent>
          <Grid container spacing={2.25}>
            <Grid size={{ xs: 12, md: 6, lg: 6 }}>
              <Card
                sx={{
                  border: "1px solid #D7DAE7",
                }}
              >
                <CardHeader
                  title={
                    <Stack
                      direction={"row"}
                      justifyContent={"space-between"}
                      alignItems={"center"}
                    >
                      <Typography variant="body1" fontWeight={500}>
                        Energy Production
                      </Typography>
                      <CustomOutlinedSelect
                        placeholder={"Select"}
                        options={selectOptions}
                        width="fit-content"
                      />
                    </Stack>
                  }
                />
                <Divider />
                <CardContent sx={{ height: "400px" }}>
                  <ProsumerDashboardLineChart
                    usageData={[36, 101, 66, 86, 41, 38, 45]}
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 6 }}>
              <Card
                sx={{
                  border: "1px solid #D7DAE7",
                }}
              >
                <CardHeader
                  title={
                    <Stack
                      direction={"row"}
                      justifyContent={"space-between"}
                      alignItems={"center"}
                    >
                      <Typography variant="body1" fontWeight={500}>
                        Energy Consumption
                      </Typography>
                      <CustomOutlinedSelect
                        placeholder={"Select"}
                        options={selectOptions}
                        width="fit-content"
                      />
                    </Stack>
                  }
                />
                <Divider />
                <CardContent sx={{ height: "400px" }}>
                  <ProsumerDashboardLineChart
                    usageData={["105", "15", "70", "36", "36", "68", "116"]}
                    color="#000000"
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 6 }}>
              <Card
                sx={{
                  border: "1px solid #D7DAE7",
                }}
              >
                <CardHeader
                  title={
                    <Stack
                      direction={"row"}
                      justifyContent={"space-between"}
                      alignItems={"center"}
                    >
                      <Typography variant="body1" fontWeight={500}>
                        Transacted
                      </Typography>
                      <CustomOutlinedSelect
                        placeholder={"Select"}
                        options={selectOptions}
                        width="fit-content"
                      />
                    </Stack>
                  }
                />
                <Divider />
                <CardContent sx={{ height: "400px" }}>
                  <ProsumerDashboardLineChart
                    usageData={[9, 105, 12, 40, 72, 41, 82]}
                    color="#000000"
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 6 }}>
              <Card
                sx={{
                  border: "1px solid #D7DAE7",
                }}
              >
                <CardHeader
                  title={
                    <Stack
                      direction={"row"}
                      justifyContent={"space-between"}
                      alignItems={"center"}
                    >
                      <Typography variant="body1" fontWeight={500}>
                        Grid Interaction
                      </Typography>
                      <CustomOutlinedSelect
                        placeholder={"Select"}
                        options={selectOptions}
                        width="fit-content"
                      />
                    </Stack>
                  }
                />
                <Divider />
                <CardContent sx={{ height: "400px", width: "100%" }}>
                  <ProsumerDashboardBarChart />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Layout>
  );
};

export default ProsumerDashboard;
